import { useCallback, useMemo } from 'react';
import count from 'transmute/count';
import I18n from 'I18n';
import MessageHistoryCore from 'conversations-message-history/web/message-history/components/MessageHistoryCore';
import ThemeConfigProvider from 'conversations-visitor-message-history/theming/providers/ThemeConfigProvider';
import SpotlightThemeProvider from 'conversations-visitor-experience-components/visitor-widget/spotlight/SpotlightThemeProvider';
import useIsSpotlightLauncher from '../../hooks/useIsSpotlightLauncher';
import { useAppDispatch } from '../../buildStore';
import { navigateToStagedThread } from '../../navigation/actions/navigateToStagedThread';
import { MessageHistoryConfig } from '../constants/MessageHistoryConfig';
// @ts-ignore - No type definitions available
import ConnectedThreadHistoryFetcher from '../containers/ConnectedThreadHistoryFetcher';
import { buildMessageHistoryTheme, buildSpotlightMessageHistoryTheme } from 'conversations-visitor-message-history/utils/buildMessageHistoryTheme';
// @ts-ignore - No type definitions available
import ConnectedNewThreadPrompt from '../../thread-view/containers/ConnectedNewThreadPrompt';
import SpotlightNewThreadPrompt from '../../thread-create/components/SpotlightNewThreadPrompt';
import { AriaLiveContextProvider } from 'visitor-ui-component-library/utils/aria-live/AriaLiveContext';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const MessageHistoryWrapper = styled.div.withConfig({
  displayName: "ThreadHistory__MessageHistoryWrapper"
})(["z-index:0;padding-block-start:4px;padding-inline:16px;flex-shrink:0;@container chat-widget (max-width:900px){padding-inline:8px;}"]);
const ThreadHistory = ({
  coloring,
  messagesList,
  portalId,
  senders,
  shouldRenderNewThreadPrompt,
  isChatTransferred
}) => {
  const dispatch = useAppDispatch();
  const isSpotlight = useIsSpotlightLauncher();
  const handleCreateNewThread = useCallback(() => dispatch(navigateToStagedThread()), [dispatch]);
  const themeConfig = isSpotlight ? buildSpotlightMessageHistoryTheme({
    coloring
  }) : buildMessageHistoryTheme({
    coloring
  });
  // The Initial Message Text Box Does not use the same rich text editor as the rest of the messages
  // this is a temporary fix to make sure that the new lines are rendered as <br> tags until we
  // can refactor the initialMessages text box to use the same editor as the rest of the messages
  const messages = useMemo(() => messagesList.map(message => {
    if (message.get('@type') === 'INITIAL_MESSAGE') {
      const richText = message.get('richText');
      return richText ? message.set('richText', richText.replace(/\n/g, '<br>')) : message;
    }
    return message;
  }).toList(), [messagesList]);
  const content = /*#__PURE__*/_jsx(ConnectedThreadHistoryFetcher, {
    messageCount: count(messagesList),
    shouldScrollToBottom: shouldRenderNewThreadPrompt,
    children: /*#__PURE__*/_jsx(ThemeConfigProvider, {
      themeConfig: themeConfig,
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(AriaLiveContextProvider, {
          children: /*#__PURE__*/_jsx("section", {
            "aria-label": I18n.text('conversations-visitor-ui.visitorExperienceAriaLabels.messageHistory'),
            children: /*#__PURE__*/_jsx(MessageHistoryWrapper, {
              children: /*#__PURE__*/_jsx(MessageHistoryCore, {
                historyConfig: MessageHistoryConfig,
                messages: messages,
                portalId: portalId,
                senders: senders
              })
            })
          })
        }), shouldRenderNewThreadPrompt && (isSpotlight ? /*#__PURE__*/_jsx(SpotlightNewThreadPrompt, {
          createNewThread: handleCreateNewThread
        }) : /*#__PURE__*/_jsx(ConnectedNewThreadPrompt, {
          isChatTransferred: isChatTransferred
        }))]
      })
    })
  });
  return isSpotlight ? /*#__PURE__*/_jsx(SpotlightThemeProvider, {
    children: content
  }) : content;
};
ThreadHistory.displayName = 'ThreadHistory';
export default ThreadHistory;